/**
 * Contacts Cache Manager
 * 
 * Standalone cache management for Webex Contacts.
 * Handles localStorage persistence with TTL and schema versioning.
 */

import type {
  WebexContactsCache,
  ContactEntry,
  ContactsCacheStats,
  ContactsTelemetryHandler,
} from './types';

/**
 * Cache manager for contacts data
 * Handles localStorage operations with error handling and telemetry
 */
export class ContactsCacheManager {
  private static readonly CACHE_KEY_PREFIX = 'webexContacts';
  private static readonly CACHE_VERSION = 1;
  private static readonly DEFAULT_TTL_MS = 12 * 60 * 60 * 1000; // 12 hours

  private userId: string;
  private cacheTTL: number;
  private telemetry?: ContactsTelemetryHandler;

  constructor(
    userId: string,
    cacheTTL: number = ContactsCacheManager.DEFAULT_TTL_MS,
    telemetry?: ContactsTelemetryHandler
  ) {
    this.userId = userId;
    this.cacheTTL = cacheTTL;
    this.telemetry = telemetry;
  }

  /**
   * Generate cache key for current user
   */
  private getCacheKey(): string {
    return `${ContactsCacheManager.CACHE_KEY_PREFIX}:v${ContactsCacheManager.CACHE_VERSION}:${this.userId}`;
  }

  /**
   * Load cache from localStorage
   * Returns null if cache doesn't exist, is invalid, or expired
   */
  load(): WebexContactsCache | null {
    const startTime = Date.now();

    try {
      const key = this.getCacheKey();
      const raw = localStorage.getItem(key);

      if (!raw) {
        console.log('[Contacts] Cache: No cache found in localStorage');
        return null;
      }

      const cached = JSON.parse(raw) as WebexContactsCache;

      // Validate schema version
      if (cached.version !== ContactsCacheManager.CACHE_VERSION) {
        console.warn('[Contacts] Cache: Version mismatch', {
          expected: ContactsCacheManager.CACHE_VERSION,
          found: cached.version,
        });
        this.clear(); // Clear incompatible cache
        return null;
      }

      // Validate user scope
      if (cached.userId !== this.userId) {
        console.warn('[Contacts] Cache: User mismatch', {
          expected: this.userId,
          found: cached.userId,
        });
        return null;
      }

      // Check if expired
      if (this.isExpired(cached)) {
        const age = Date.now() - cached.fetchedAt;
        console.log('[Contacts] Cache: Cache expired', {
          age,
          expiresAt: new Date(cached.expiresAt).toISOString(),
        });

        this.telemetry?.emit({
          type: 'cache_expired',
          age,
        });

        return null;
      }

      const duration = Date.now() - startTime;
      const age = Date.now() - cached.fetchedAt;

      console.log('[Contacts] Cache: Loaded from localStorage', {
        totalContacts: cached.totalContacts,
        totalPhones: Object.keys(cached.phoneMap).length,
        age,
        duration,
      });

      this.telemetry?.emit({
        type: 'cache_load_success',
        duration,
        totalContacts: cached.totalContacts,
        age,
      });

      return cached;
    } catch (error: any) {
      console.error('[Contacts] Cache: Failed to load', {
        error: error.message,
      });
      return null;
    }
  }

  /**
   * Save cache to localStorage
   */
  save(phoneMap: Record<string, ContactEntry>): WebexContactsCache {
    const startTime = Date.now();

    const now = Date.now();
    const cache: WebexContactsCache = {
      version: ContactsCacheManager.CACHE_VERSION,
      userId: this.userId,
      fetchedAt: now,
      expiresAt: now + this.cacheTTL,
      totalContacts: this.countUniqueContacts(phoneMap),
      phoneMap,
    };

    try {
      const key = this.getCacheKey();
      const raw = JSON.stringify(cache);

      localStorage.setItem(key, raw);

      const duration = Date.now() - startTime;
      const size = raw.length;

      console.log('[Contacts] Cache: Saved to localStorage', {
        totalContacts: cache.totalContacts,
        totalPhones: Object.keys(phoneMap).length,
      });

      this.telemetry?.emit({
        type: 'cache_save_success',
        duration,
        size,
      });

      return cache;
    } catch (error: any) {
      console.error('[Contacts] Cache: Failed to save', {
        error: error.message,
        name: error.name,
      });

      // Handle quota exceeded
      if (error.name === 'QuotaExceededError') {
        const size = JSON.stringify(cache).length;
        console.error('[Contacts] Cache: localStorage quota exceeded', {
          cacheSize: size,
        });

        this.telemetry?.emit({
          type: 'storage_quota_exceeded',
          cacheSize: size,
        });
      }

      // Return cache even if save failed (for in-memory use)
      return cache;
    }
  }

  /**
   * Clear cache from localStorage
   */
  clear(): void {
    try {
      const key = this.getCacheKey();
      localStorage.removeItem(key);
      console.log('[Contacts] Cache: Cleared from localStorage');
    } catch (error: any) {
      console.error('[Contacts] Cache: Failed to clear', {
        error: error.message,
      });
    }
  }

  /**
   * Check if cache is expired
   */
  isExpired(cache: WebexContactsCache): boolean {
    return Date.now() >= cache.expiresAt;
  }

  /**
   * Get cache statistics
   */
  getStats(cache: WebexContactsCache | null): ContactsCacheStats {
    if (!cache) {
      return {
        totalContacts: 0,
        totalPhones: 0,
        fetchedAt: 0,
        expiresAt: 0,
        isExpired: true,
        cacheSize: 0,
        userId: this.userId,
      };
    }

    const cacheSize = JSON.stringify(cache).length;

    return {
      totalContacts: cache.totalContacts,
      totalPhones: Object.keys(cache.phoneMap).length,
      fetchedAt: cache.fetchedAt,
      expiresAt: cache.expiresAt,
      isExpired: this.isExpired(cache),
      cacheSize,
      userId: cache.userId,
    };
  }

  /**
   * Count unique contacts in phone map
   * (Since phone map is denormalized, we need to count unique contactIds)
   */
  private countUniqueContacts(phoneMap: Record<string, ContactEntry>): number {
    const uniqueContactIds = new Set<string>();
    for (const entry of Object.values(phoneMap)) {
      uniqueContactIds.add(entry.contactId);
    }
    return uniqueContactIds.size;
  }

  /**
   * Migrate old cache schemas (if needed in future)
   */
  migrate(): void {
    // Check for old schema versions and migrate
    // For now, just clear old versions
    try {
      const oldKeys = [
        `webexContacts:${this.userId}`, // No version prefix
        `contacts:${this.userId}`,       // Different prefix
      ];

      for (const oldKey of oldKeys) {
        if (localStorage.getItem(oldKey)) {
          console.log('[Contacts] Cache: Removing old cache format', { oldKey });
          localStorage.removeItem(oldKey);
        }
      }
    } catch (error: any) {
      console.error('[Contacts] Cache: Migration failed', {
        error: error.message,
      });
    }
  }
}
